import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Suspense, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "./context/auth/AuthContext";
import Loader from "./components/custom/Loader";
import ProtectedRoute from "./routes/ProtectedRoute";
import Layout from "./components/layout";

const Login = lazy(() => import("./pages/Login/Login"));
const DashboardList = lazy(() => import("./pages/Dashboard/DashboardList"));
const CustomerFirmList = lazy(() =>
  import("./pages/Customer Firm/CustomerFirmList")
);
const AddUpdateCustomer = lazy(() =>
  import("./pages/Customer Firm/AddUpdateCustomer")
);
const AdminUserList = lazy(() => import("./pages/Admin Users/AdminUserList"));
const AddUpdateAdminUser = lazy(() =>
  import("./pages/Admin Users/AddUpdateAdminUser")
);
const EmployeeList = lazy(() => import("./pages/Employee/EmployeeList"));
const AddUpdateEmployee = lazy(() =>
  import("./pages/Employee/AddUpdateEmployee")
);
const ProductList = lazy(() => import("./pages/Products/ProductList"));
const LeadManagementList = lazy(() =>
  import("./pages/Lead and Quotation/Lead Management/LeadManagementList")
);
const AddUpdateLead = lazy(() =>
  import("./pages/Lead and Quotation/Lead Management/AddUpdateLead")
);
const LeadActivityLog = lazy(() =>
  import("./pages/Lead and Quotation/Lead Management/LeadActivityLog")
);
const LeadFollowupList = lazy(() =>
  import("./pages/Lead and Quotation/Lead Followup/LeadFollowupList")
);
const QuotationList = lazy(() =>
  import("./pages/Lead and Quotation/Quotation/QuotationList")
);
const QuotationActivityLog = lazy(() =>
  import("./pages/Lead and Quotation/Quotation/QuotationActivityLog")
);
const QuotationPreview = lazy(() =>
  import("./pages/Lead and Quotation/Quotation/QuotationPreview")
);
const QuotationLogList = lazy(() =>
  import("./pages/Lead and Quotation/Quotation/QuotationLogList")
);
const AddUpdateQuotation = lazy(() =>
  import("./pages/Lead and Quotation/Quotation/AddUpdateQuotation")
);
const WorkOrderList = lazy(() =>
  import("./pages/Lead and Quotation/Quotation/WorkOrderList")
);
const WorkOrderActivityLog = lazy(() =>
  import("./pages/Lead and Quotation/Quotation/WorkOrderActivityLog")
);
const OfficeTeamStatusList = lazy(() =>
  import("./pages/Todays Team Status/OfficeTeamStatusList")
);
const FieldTeamStatusList = lazy(() =>
  import("./pages/Todays Team Status/FieldTeamStatusList")
);
const WeeklyReportList = lazy(() =>
  import("./pages/Reports/Weekly/WeeklyReportList")
);
const WeeklyReportDetail = lazy(() =>
  import("./pages/Reports/Weekly/WeeklyReportDetail")
);
const LeaveReportList = lazy(() =>
  import("./pages/Reports/Leave/LeaveReportList")
);
const LeaveList = lazy(() => import("./pages/Leave/LeaveList"));
const RegularizationReportList = lazy(() =>
  import("./pages/Reports/Regularization/RegularizationReportList")
);
const DesignationList = lazy(() =>
  import("./pages/Master/Designation/DesignationList")
);
const QuotationFormatList = lazy(() =>
  import("./pages/Master/Quotation No Format/QuotationFormatList")
);
const TermsConditionsList = lazy(() =>
  import("./pages/Master/Terms And Conditions/TermsConditionsList")
);
const OfficeList = lazy(() => import("./pages/Master/Office/OfficeList"));
const TableHeaderList = lazy(() =>
  import("./pages/Master/Table Header/TableHeaderList")
);
const ActivityTypeList = lazy(() =>
  import("./pages/Master/Activity Type/ActivityTypeList")
);
const TicketList = lazy(() => import("./pages/Tickets/TicketList"));

function App() {
  return (
    <AuthProvider>
      <HelmetProvider>
        <BrowserRouter>
          <Suspense fallback={<Loader fullScreen delay={150} label="Preparing Elee Controls..." />}>
            <Routes>
              <Route path="/login" element={<Login />} />

                <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardList />} />
                  <Route path="/admin-users" element={<AdminUserList />} />
                  <Route path="/admin-users/add" element={<AddUpdateAdminUser />} />
                  <Route path="/admin-users/update/:id" element={<AddUpdateAdminUser />} />
                  <Route path="/customer-firm" element={<CustomerFirmList />} />
                  <Route path="/customer-firm/add" element={<AddUpdateCustomer />} />
                  <Route path="/customer-firm/update/:id" element={<AddUpdateCustomer />} />
                  <Route path="/customer-firm/view/:id" element={<AddUpdateCustomer />} />
                  <Route path="/employees" element={<EmployeeList />} />
                  <Route path="/employees/add" element={<AddUpdateEmployee />} />
                  <Route path="/employees/update/:id" element={<AddUpdateEmployee />} />
                  <Route path="/employees/view/:id" element={<AddUpdateEmployee />} />
                  <Route path="/products" element={<ProductList />} />
                  <Route path="/lead-management" element={<LeadManagementList />} />
                  <Route path="/lead-management/add" element={<AddUpdateLead />} />
                  <Route path="/lead-management/update/:id" element={<AddUpdateLead />} />
                  <Route path="/lead-management/view/:id" element={<AddUpdateLead />} />
                  <Route path="/lead-management/activity-log/:id" element={<LeadActivityLog />} />
                  <Route path="/lead-management/followup/:id" element={<LeadFollowupList />} />
                  <Route path="/lead-management/quotation/:leadId" element={<AddUpdateQuotation />} />
                  <Route path="/quotation" element={<QuotationList />} />
                  <Route path="/quotation/preview/:id" element={<QuotationPreview />} />
                  <Route path="/work-order" element={<WorkOrderList />} />
                  <Route path="/work-order/activity-log/:leadId/:quotationId" element={<WorkOrderActivityLog />} />
                  <Route path="/quotation/activity-log/:id" element={<QuotationActivityLog />} />
                  <Route path="/quotation/log-list" element={<QuotationLogList />} />
                  <Route path="/office" element={<OfficeList />} />
                  <Route path="/designation" element={<DesignationList />} />
                  <Route path="/quotation-no-format" element={<QuotationFormatList />} />
                  <Route path="/table-header" element={<TableHeaderList />} />
                  <Route path="/activity-type" element={<ActivityTypeList />} />
                  <Route path="/terms-and-conditions" element={<TermsConditionsList />} />
                  <Route path="/todays-team-status/office-team" element={<OfficeTeamStatusList />} />
                  <Route path="/todays-team-status/field-team" element={<FieldTeamStatusList />} />
                  <Route path="/reports/weekly" element={<WeeklyReportList />} />
                  <Route path="/reports/weekly/:id" element={<WeeklyReportDetail />} />
                  <Route path="/leave" element={<LeaveList />} />
                  <Route path="/reports/leave" element={<LeaveReportList />} />
                  <Route path="/reports/regularization" element={<RegularizationReportList />} />
                  <Route path="/tickets" element={<TicketList />} />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </HelmetProvider>
    </AuthProvider>
  );
}

export default App;
